import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getBooking, confirmBooking, cancelBooking, exportBookingCalendar } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  CheckCircle2,
  Lock,
  X,
  RefreshCw,
  Download,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { formatRelativeTime } from '../lib/utils';
import { toast } from 'sonner';
import { ReadinessSignal, RunOfShowPanel, CrewSlotsPanel, GuestListPanel } from '../components/BookingPanels';
import { ShowReadiness } from '../components/ShowReadiness';
import { KeyContacts, TechRider } from '../components/DemoSections';
import { KeyContactsPanel } from '../components/showtime/KeyContactsPanel';
import { ParkingArrival } from '../components/showtime/ParkingArrival';
import Agreement from '../components/showtime/Agreement';
import Activity from '../components/showtime/Activity';
import ShowtimeDesktopLayout from '../components/layouts/ShowtimeDesktopLayout';
import GigGearPanel from '../components/showtime/GigGearPanel';
import { useBreakpoint } from '../components/layouts/DirectoryLayout';
import { PageSkeleton, Bone, BoneCard } from '../components/PageSkeleton';

const PAYMENT_TIMING_OPTIONS = [
  { value: 'same_night', label: 'Same night' },
  { value: 'within_24h', label: 'Within 24 hours' },
  { value: 'within_7_days', label: 'Within 7 days' },
  { value: 'within_14_days', label: 'Within 14 days' },
  { value: 'custom', label: 'Custom arrangement' },
];

const PAY_UNIT_OPTIONS = [
  { value: 'flat', label: 'Flat fee' },
  { value: 'hourly', label: 'Per hour' },
  { value: 'part_of_day', label: 'Part of day' },
];

/**
 * Returns a precise countdown string for the gig.
 * - >24h away: "In 6d 14h"
 * - <24h away (still future): "In 4h 12m"
 * - past: falls back to formatRelativeTime ("today" / "yesterday" / "X days ago")
 * Anchors on snapshot.callTime when available, otherwise start of gig day.
 */
function formatGigCountdown(dateStr, callTime) {
  if (!dateStr) return null;
  const timePart = callTime && /^\d{1,2}:\d{2}/.test(callTime) ? callTime : '00:00';
  const target = new Date(`${dateStr}T${timePart}:00`);
  if (Number.isNaN(target.getTime())) return null;
  const diffMs = target.getTime() - Date.now();
  if (diffMs <= 0) return formatRelativeTime(dateStr);
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days >= 1) return `In ${days}d ${hours}h`;
  return `In ${hours}h ${minutes}m`;
}

export default function BookingConfirmationPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDesktop } = useBreakpoint();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [runOfShow, setRunOfShow] = useState([]);
  const [crewSlots, setCrewSlots] = useState([]);
  const [guestList, setGuestList] = useState({ entries: [], cap: 0, approved: false });
  const [techRiderReviewed, setTechRiderReviewed] = useState(false);

  useEffect(() => {
    loadBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const loadBooking = async (showRefreshToast = false, retryCount = 0) => {
    if (showRefreshToast) setRefreshing(true);
    try {
      const response = await getBooking(bookingId);
      setBooking(response.data);
      if (showRefreshToast) toast.success('Status refreshed');
    } catch (error) {
      // Retry once after a short delay (handles cookie race condition from SHOWTIME demo)
      if (retryCount < 1 && error.response?.status === 401) {
        await new Promise(r => setTimeout(r, 1000));
        return loadBooking(showRefreshToast, retryCount + 1);
      }
      toast.error('Failed to load booking. Please refresh the page.');
      if (!booking) navigate('/requests');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => loadBooking(true);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const response = await confirmBooking(bookingId);
      toast.success(response.data.message);
      await loadBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm');
      // Reload to get fresh state in case of race condition
      await loadBooking();
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      await cancelBooking(bookingId);
      toast.success('Booking canceled');
      navigate('/my-gigs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleExportCalendar = async () => {
    setExporting(true);
    try {
      const response = await exportBookingCalendar(bookingId);
      // Guard against blob responses that contain an error payload (axios returns 2xx as blob but
      // if the server unexpectedly returns JSON, catch it here before writing a corrupt .ics).
      const contentType = response.headers?.['content-type'] || '';
      if (!contentType.includes('text/calendar')) {
        const text = await response.data.text();
        try {
          const parsed = JSON.parse(text);
          throw new Error(parsed.detail || 'Failed to export calendar');
        } catch {
          throw new Error('Failed to export calendar');
        }
      }
      // Create download link
      const blob = new Blob([response.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stage8-booking-${bookingId.slice(0, 8)}.ics`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Calendar event downloaded');
    } catch (error) {
      // When axios is in blob mode, error.response.data is also a Blob — not JSON.
      let message = 'Failed to export calendar';
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) message = parsed.detail;
        } catch { /* keep default */ }
      } else if (typeof data?.detail === 'string') {
        message = data.detail;
      } else if (error.message && error.message !== 'Failed to export calendar') {
        message = error.message;
      }
      toast.error(message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="content-container">
        <PageSkeleton label="Loading showtime">
          <div className="flex items-center justify-between">
            <Bone w={120} h={14} />
            <Bone w={80} h={22} r="pill" />
          </div>
          <div className="flex items-start justify-between gap-4 mt-6">
            <div className="flex-1">
              <Bone w="80%" h={34} tone="card" />
              <Bone w="56%" h={34} tone="card" className="mt-2" />
            </div>
            <Bone w={150} h={36} r="md" />
          </div>
          <Bone w="88%" h={14} className="mt-4" />
          <Bone w="64%" h={14} className="mt-2" />
          <Bone w={164} h={36} r="pill" className="mt-4" />
          <BoneCard className="p-4 mt-5 flex justify-center gap-2">
            <Bone w={64} h={26} r="pill" /><Bone w={52} h={26} r="pill" /><Bone w={64} h={26} r="pill" /><Bone w={56} h={26} r="pill" />
          </BoneCard>
          <BoneCard className="mt-5 overflow-hidden">
            <div className="p-5 flex items-start justify-between gap-4">
              <div className="flex-1"><Bone w="60%" h={20} /><Bone w="80%" h={12} className="mt-3" /></div>
              <Bone w={112} h={32} r="pill" />
            </div>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="px-5 py-4 flex items-center justify-between gap-4" style={{ borderTop: '1px solid var(--s8-b1-solid)' }}>
                <Bone w="46%" h={16} />
                <Bone w={100} h={30} r="sm" />
              </div>
            ))}
          </BoneCard>
        </PageSkeleton>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="content-container">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Booking not found</p>
        </div>
      </div>
    );
  }

  const isVenue = user?.profileType === 'VENUE';
  const isTalent = !isVenue;
  const snapshot = booking.snapshot || {};
  
  const isProposed = booking.status === 'PROPOSED';
  const isConfirmed = booking.status === 'BASICS_CONFIRMED';
  const isCanceled = booking.status === 'CANCELED';
  
  const venueConfirmed = !!booking.venueConfirmedAt;
  const talentConfirmed = !!booking.talentConfirmedAt;
  const bothConfirmed = venueConfirmed && talentConfirmed;
  
  const userConfirmed = isVenue ? venueConfirmed : talentConfirmed;
  
  const canConfirm = isProposed && !userConfirmed;

  const getPaymentTimingLabel = (value) => {
    return PAYMENT_TIMING_OPTIONS.find(o => o.value === value)?.label || value;
  };

  const getPayUnitLabel = (value) => {
    return PAY_UNIT_OPTIONS.find(o => o.value === value)?.label || value;
  };

  return (
    <ShowtimePageBody
      isDesktop={isDesktop}
      bookingId={bookingId}
      booking={booking}
      snapshot={snapshot}
      isVenue={isVenue}
      isProposed={isProposed}
      isConfirmed={isConfirmed}
      isCanceled={isCanceled}
      bothConfirmed={bothConfirmed}
      userConfirmed={userConfirmed}
      canConfirm={canConfirm}
      confirming={confirming}
      refreshing={refreshing}
      exporting={exporting}
      onRefresh={handleRefresh}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      onExportCalendar={handleExportCalendar}
      runOfShow={runOfShow}
      setRunOfShow={setRunOfShow}
      crewSlots={crewSlots}
      setCrewSlots={setCrewSlots}
      guestList={guestList}
      setGuestList={setGuestList}
      techRiderReviewed={techRiderReviewed}
      setTechRiderReviewed={setTechRiderReviewed}
      getPayUnitLabel={getPayUnitLabel}
      getPaymentTimingLabel={getPaymentTimingLabel}
    />
  );
}

/* ─── Page body: builds JSX slots once and renders either the desktop
   7/5 grid or the mobile single-column stack based on viewport. ─── */

function ShowtimePageBody({
  isDesktop,
  bookingId,
  booking,
  snapshot,
  isVenue,
  isProposed,
  isConfirmed,
  isCanceled,
  bothConfirmed,
  userConfirmed,
  canConfirm,
  confirming,
  refreshing,
  exporting,
  onRefresh,
  onConfirm,
  onCancel,
  onExportCalendar,
  runOfShow,
  setRunOfShow,
  crewSlots,
  setCrewSlots,
  guestList,
  setGuestList,
  techRiderReviewed,
  setTechRiderReviewed,
  getPayUnitLabel,
  getPaymentTimingLabel,
}) {
  /* Header strip — title, meta, Confirmed badge, Add to Calendar */
  const header = (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/my-gigs"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to My Gigs
        </Link>
        <div className="flex items-center gap-2">
          {isProposed && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing}
              className="text-muted-foreground hover:text-foreground"
              data-testid="refresh-status-btn"
            >
              <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
            </Button>
          )}
          <Badge
            className={cn(
              isConfirmed && "bg-green-500/20 text-green-400 border-green-500/50",
              isProposed && "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
              isCanceled && "bg-red-500/20 text-red-400 border-red-500/50"
            )}
          >
            {isConfirmed ? 'Confirmed' : isProposed ? 'Awaiting Confirmation' : 'Canceled'}
          </Badge>
        </div>
      </div>

      <div className="flex items-start justify-between mb-2">
        <h1
          style={{
            fontFamily: 'var(--s8-font-display)',
            fontWeight: 600,
            fontSynthesis: 'none',
            fontSize: 'clamp(1.875rem, 1.875rem + 1vw, 2.25rem)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            textWrap: 'balance',
            color: 'var(--s8-text-main)',
          }}
          data-testid="booking-page-title"
        >
          {snapshot.title || booking.request?.title || 'Show Overview'}
        </h1>
        {isConfirmed && (
          <button
            onClick={onExportCalendar}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2.5 text-xs font-medium uppercase tracking-wider transition-all hover:brightness-110 shrink-0 ml-4"
            style={{
              fontFamily: 'var(--s8-font-mono)',
              color: 'var(--s8-amber)',
              background: 'rgba(200,146,62,0.08)',
              border: '1px solid rgba(200,146,62,0.2)',
              borderRadius: 'var(--s8-radius-md)',
            }}
            data-testid="export-calendar-btn"
          >
            <Download size={14} />
            {exporting ? 'Exporting…' : 'Add to Calendar'}
          </button>
        )}
      </div>
      <p
        className="text-xs uppercase tracking-wider"
        style={{ fontFamily: 'var(--s8-font-mono)', color: 'var(--s8-text-muted)' }}
        data-testid="booking-header-line2"
      >
        {(() => {
          const countdown = formatGigCountdown(snapshot.date, snapshot.callTime);
          return countdown ? (
            <>
              <span style={{ color: 'var(--s8-amber)', fontWeight: 600 }} data-testid="showtime-countdown">
                {countdown}
              </span>
              {' · '}
            </>
          ) : null;
        })()}
        {snapshot.date ? new Date(snapshot.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : ''}
        {snapshot.callTime ? ` · ${snapshot.callTime} call` : ''}{snapshot.role ? ` · ${snapshot.role}` : ''}
      </p>
      <p
        className="text-[11px] uppercase tracking-wider mt-1"
        style={{ fontFamily: 'var(--s8-font-mono)', color: 'var(--s8-text-dim)' }}
        data-testid="booking-header-line3"
      >
        {snapshot.fee ? `€${snapshot.fee}` : ''}{snapshot.payUnit ? ` ${getPayUnitLabel(snapshot.payUnit).toLowerCase()}` : ''}{snapshot.paymentTiming ? ` · ${getPaymentTimingLabel(snapshot.paymentTiming)}` : ''} · 2nd appearance
      </p>
      <div
        className="inline-flex items-center gap-2 mt-3 px-3 py-1.5"
        style={{
          background: 'var(--s8-bg-card)',
          border: '1px solid var(--s8-border-card)',
          borderRadius: 'var(--s8-radius-pill)',
        }}
        data-testid="booking-header-ref"
      >
        <span
          className="text-[10px] font-semibold tracking-widest"
          style={{ color: 'var(--s8-text-dim)' }}
        >
          REF
        </span>
        <span
          className="text-sm tracking-wider"
          style={{
            fontFamily: 'var(--s8-font-mono)',
            color: 'var(--s8-amber)',
          }}
        >
          S8-7K2M-9X4P
        </span>
      </div>
    </div>
  );

  const readinessSignal = (
    <ReadinessSignal
      booking={booking}
      crewSlots={crewSlots}
      guestList={guestList}
      runOfShow={runOfShow}
    />
  );

  const showReadiness = isConfirmed ? (
    <ShowReadiness
      crewSlots={crewSlots}
      runOfShow={runOfShow}
      guestList={guestList}
      snapshot={snapshot}
      logistics={booking.logistics}
      isDemo={!!booking.demo}
      techRiderReviewed={techRiderReviewed}
    />
  ) : null;

  const agreement = (
    <Agreement
      booking={booking}
      snapshot={snapshot}
      getPayUnitLabel={getPayUnitLabel}
      getPaymentTimingLabel={getPaymentTimingLabel}
    />
  );

  // Key Contacts — demo bookings keep the curated showcase (DEMO_CONTACTS);
  // real bookings get the live panel from booking.dayOf.contacts + the artist as
  // a first-party contact. Artist phone is never exposed raw (privacy invariant);
  // the artist's reach is the server-provided WhatsApp link, present once basics
  // are confirmed. Self-hides when there are no contacts and no resolvable artist.
  const keyContacts = booking.demo ? (
    <KeyContacts />
  ) : (
    <KeyContactsPanel
      contacts={booking.dayOf?.contacts}
      artist={{
        name: booking.talentProfile?.displayName || snapshot.talentName || booking.talentName,
        role: booking.talentProfile?.roleSubtype || 'Artist',
        whatsappLink: booking.talentWhatsappLink || null,
      }}
    />
  );

  // Parking & Arrival — reads the day-of bundle propagated onto the booking
  // (Element 1 contract: booking.dayOf.parking) + the locked venue address.
  // Self-hides when empty, so it no-ops until the parked parking branch lands.
  const parkingArrival = (
    <ParkingArrival
      parking={booking.dayOf?.parking}
      venueAddress={snapshot.venueAddress}
    />
  );

  const techRider = booking.demo ? (
    <TechRider
      reviewed={techRiderReviewed}
      onMarkReviewed={() => setTechRiderReviewed(true)}
    />
  ) : null;

  const actionButtons = !isCanceled ? (
    <div className="space-y-3">
      {canConfirm && (
        <Button
          onClick={onConfirm}
          disabled={confirming}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-6"
          data-testid="confirm-basics-btn"
        >
          {confirming ? (
            'Confirming...'
          ) : (
            <>
              <CheckCircle2 className="h-5 w-5 mr-2" />
              Confirm Basics and Unlock WhatsApp
            </>
          )}
        </Button>
      )}

      {userConfirmed && !bothConfirmed && (
        <div className="text-center p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
          <p className="text-yellow-400 text-sm">
            You've confirmed! Waiting for {isVenue ? 'talent' : 'venue'} to confirm.
          </p>
        </div>
      )}

      {isProposed && (
        <Button
          variant="outline"
          onClick={onCancel}
          className="w-full border-red-500/50 text-red-400 hover:bg-red-500/20"
          data-testid="cancel-booking-btn"
        >
          <X className="h-4 w-4 mr-2" />
          Cancel Booking
        </Button>
      )}
    </div>
  ) : null;

  const runOfShowPanel = (
    <RunOfShowPanel
      bookingId={bookingId}
      slots={runOfShow}
      setSlots={setRunOfShow}
      summary={runOfShow.length > 0 ? `${runOfShow.length} items · ${runOfShow[0]?.time} load-in → ${runOfShow[runOfShow.length-1]?.time} ${runOfShow[runOfShow.length-1]?.label?.toLowerCase()}` : undefined}
    />
  );

  const crewSlotsPanel = (
    <CrewSlotsPanel
      bookingId={bookingId}
      slots={crewSlots}
      setSlots={setCrewSlots}
      isVenue={isVenue}
      summary={crewSlots.length > 0 ? `${crewSlots.filter(s => s.assigned_to).length} of ${crewSlots.length} assigned${crewSlots.find(s => !s.assigned_to) ? ` · ${crewSlots.find(s => !s.assigned_to).role} pending` : ''}` : undefined}
    />
  );

  const guestListPanel = (
    <GuestListPanel
      bookingId={bookingId}
      guestList={guestList}
      setGuestList={setGuestList}
      isVenue={isVenue}
      summary={`${(guestList?.entries || []).reduce((n, e) => n + 1 + (e.plus_one ? 1 : 0), 0)} of ${guestList?.cap || '—'} spots used`}
    />
  );

  const activity = booking.demo ? <Activity /> : null;

  // Talent's per-gig gear override (musician bookings only; not shown to venue)
  const gearOverride = (!isVenue && !booking.demo && booking.talentType === 'MUSICIAN') ? (
    <GigGearPanel bookingId={bookingId} gearOverride={booking.gear_override ?? null} />
  ) : null;

  /* ═══════ DESKTOP (>=1280px): full-width band + 7/5 grid ═══════ */
  if (isDesktop) {
    return (
      <ShowtimeDesktopLayout
        header={header}
        readinessSignal={readinessSignal}
        showReadiness={showReadiness}
        actionButtons={actionButtons}
        runOfShow={runOfShowPanel}
        techRider={techRider}
        crewSlots={crewSlotsPanel}
        gearOverride={gearOverride}
        agreement={agreement}
        keyContacts={keyContacts}
        parking={parkingArrival}
        guestList={guestListPanel}
        activity={activity}
      />
    );
  }

  /* ═══════ MOBILE / TABLET (<1280px): existing single-column stack ═══════ */
  return (
    <div className="py-6 max-w-4xl mx-auto">
      {header}
      {readinessSignal}
      {showReadiness}
      {agreement}
      {keyContacts}
      {parkingArrival}
      {techRider}
      {gearOverride}
      {actionButtons}

      {/* ─── Operational Panels ─── */}
      <div className="mt-6">
        {runOfShowPanel}
        {crewSlotsPanel}
        {guestListPanel}
      </div>

      {/* Activity — appended at bottom of mobile stack (demo only) */}
      {activity && (
        <div className="mt-6">
          {activity}
        </div>
      )}
    </div>
  );
}

/* ─── Agreement section now lives in src/components/showtime/Agreement.jsx ─── */

